import { Check } from 'lucide-react'

/**
 * Explore → Connect → Verify → Fund → Rent, the progression of plan §4.3.
 *
 * The same steps render in every workspace state, which is the point: a user
 * who connects a wallet should recognise the page they were already on rather
 * than arrive at a second, unfamiliar dashboard. Only the marks change, and
 * once every mark is a check the guide has nothing left to say and goes away.
 */

export type GuideStepId = 'explore' | 'connect' | 'verify' | 'fund' | 'rent'
export type GuideStepState = 'done' | 'active' | 'todo'
export type GuideProgress = Record<GuideStepId, GuideStepState>

const ORDER: readonly GuideStepId[] = ['explore', 'connect', 'verify', 'fund', 'rent']

/**
 * Progress is derived, never stored.
 *
 * There is no separate onboarding flag to drift out of step with reality: a
 * wallet is connected or it is not, billing has approved the holder or it has
 * not, the balance is above zero or it is not, and an instance exists or it
 * does not. The marks follow from those facts alone.
 *
 * `verified` and `funded` are `undefined` when billing could not be asked; an
 * unknown fact is never drawn as a check. An instance settles both: a tenant
 * who has already rented has finished the progression, however their money
 * reached the chain, and is not sent back to a step they never needed.
 *
 * The active step is the first one not yet done, so exactly one step is
 * current until the last is finished.
 */
export function guideProgress({
  walletConnected,
  verified,
  funded,
  hasInstance = false,
}: {
  walletConnected: boolean
  verified?: boolean | undefined
  funded?: boolean | undefined
  hasInstance?: boolean
}): GuideProgress {
  const done: Record<GuideStepId, boolean> = {
    explore: walletConnected,
    connect: walletConnected,
    verify: walletConnected && (hasInstance || verified === true),
    fund: walletConnected && (hasInstance || funded === true),
    rent: walletConnected && hasInstance,
  }
  const active = ORDER.find((id) => !done[id])
  return Object.fromEntries(
    ORDER.map((id) => [id, done[id] ? 'done' : id === active ? 'active' : 'todo']),
  ) as GuideProgress
}

/** True when every step is done — the guide's cue to leave the page. */
export function guideComplete(progress: GuideProgress): boolean {
  return ORDER.every((id) => progress[id] === 'done')
}

const STEPS: Array<{ id: GuideStepId; title: string; hint: string }> = [
  { id: 'explore', title: 'Explore', hint: 'Try examples' },
  { id: 'connect', title: 'Connect', hint: 'Link your wallet' },
  { id: 'verify', title: 'Verify', hint: 'Confirm identity' },
  { id: 'fund', title: 'Fund', hint: 'Add money' },
  { id: 'rent', title: 'Rent', hint: 'Launch your own' },
]

/** Spoken, not shown — so the state of a step never rests on its colour. */
const SPOKEN: Record<GuideStepState, string> = {
  done: 'Completed',
  active: 'Current step',
  todo: 'Not started',
}

export function GuideSteps({
  progress,
  onConnect,
  onVerify,
  onFund,
}: {
  progress: GuideProgress
  /** Makes the Connect step actionable while it is still outstanding. */
  onConnect?: (() => void) | undefined
  /** Makes the Verify step actionable while it is the current one. */
  onVerify?: (() => void) | undefined
  /** Makes the Fund step actionable while it is the current one. */
  onFund?: (() => void) | undefined
}) {
  // Connect opens a sheet, so it is offered from the start. Verify and Fund
  // lead to billing, which can do nothing for a step whose turn has not come.
  const actions: Partial<Record<GuideStepId, { action: (() => void) | undefined; when: (state: GuideStepState) => boolean }>> = {
    connect: { action: onConnect, when: (state) => state !== 'done' },
    verify: { action: onVerify, when: (state) => state === 'active' },
    fund: { action: onFund, when: (state) => state === 'active' },
  }

  return (
    <ol className="overview-steps" aria-label="Getting started">
      {STEPS.map((step, index) => {
        const state = progress[step.id]
        const body = (
          <>
            <span className={`overview-step-mark overview-step-mark-${state}`} aria-hidden="true">
              {state === 'done' ? <Check size={16} strokeWidth={2.5} /> : index + 1}
            </span>
            <span className="overview-step-copy">
              <strong>{step.title}</strong>
              <span>{step.hint}</span>
            </span>
            <span className="sr-only">{SPOKEN[state]}</span>
          </>
        )
        const entry = actions[step.id]
        const actionable = entry?.action && entry.when(state) ? entry.action : undefined

        return (
          <li
            key={step.id}
            className={`overview-step overview-step-${state}`}
            {...(state === 'active' ? { 'aria-current': 'step' as const } : {})}
          >
            {actionable ? (
              <button type="button" className="overview-step-action" onClick={actionable}>
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        )
      })}
    </ol>
  )
}
